import type { StatementModel, WorkbenchState } from '../../utils/useStatementModel';
import { RuleChip } from './RuleChip';
import { ManualAdjustments } from './ManualAdjustments';

interface RulesRailProps {
  state: WorkbenchState;
  model: StatementModel;
  onChange: (patch: Partial<WorkbenchState>) => void;
}

/**
 * Plug-and-play rules rail: locked core rules, toggleable what-if rules with
 * live dollar impact, and manual adjustment lines.
 */
export function RulesRail({ state, model, onChange }: RulesRailProps) {
  // Impact is computed over included programs so the counterfactual matches the table.
  const bonusImpact = model.programs.reduce((s, p) => s + p.bonusCents, 0);
  const feesImpact = model.totals.feesMaterialsCents;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Core (locked)</h3>
        <div className="space-y-1.5">
          <RuleChip
            label="Cash basis (counted when paid)"
            locked
            info="Tuition counts in the month the parent's payment is received (the payment date, business time), not the month it was invoiced. Rent follows money actually collected."
          />
          <RuleChip
            label="Tuition-first allocation"
            locked
            info="When a payment covers only part of an invoice, it is applied to tuition first, then fees, then materials. So partial and multi-month payments count toward tuition (the rent base) before anything else."
          />
          <RuleChip
            label="Discounts netted"
            locked
            info="Sibling, employee, and other discounts reduce the tuition base directly. Rent is charged on tuition after discounts — not the list price. Expand a program to see the discount netted into each payment."
          />
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">What-if (toggle)</h3>
        <div className="space-y-1.5">
          <RuleChip
            label="Exclude fees & materials"
            active={state.excludeFeesMaterials}
            impactCents={feesImpact}
            info="Registration/activity fees and materials charges are kept out of the rent base — only tuition is shared with the facility. Turn off to add these dollars back into the base (as an explicit add-back line)."
            onToggle={() => onChange({ excludeFeesMaterials: !state.excludeFeesMaterials })}
          />
          <RuleChip
            label="Exclude BHCS Cash bonus"
            active={state.excludeBonus}
            impactCents={bonusImpact}
            info="When a family pays with BHCS Cash, the promotional bonus portion (non-withdrawable credit the school granted, not real money collected) is removed from the base. Turn off to add it back."
            onToggle={() => onChange({ excludeBonus: !state.excludeBonus })}
          />
        </div>
        <p className="mt-1.5 text-xs text-gray-400">
          Turning a rule off adds an explicit add-back line to the statement.
        </p>
      </div>
      <ManualAdjustments
        adjustments={state.adjustments}
        onChange={(adjustments) => onChange({ adjustments })}
      />
    </div>
  );
}
